import React, { useState, useEffect } from 'react'
import PTRestAPI from '../../utils/pt_rest_api' // Adjust the path as necessary
import { useAuth } from '../../utils/platform_auth.jsx'
import {
  useSearchParamValue,
  useSearchParamMutation,
} from '../../utils/searchParams'
import Typography from '../Typography'
import { useTranslation } from '../../utils/localization'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid'
import { motion, AnimatePresence } from 'framer-motion'

const CategoryList = ({ controller, onSelectCategory, categoryID }) => {
  const [categories, setCategories] = useState([])
  const [selectedCategoryID, setSelectedCategoryID] = useState(null)
  const [selectedCategoryTitle, setSelectedCategoryTitle] = useState('') // Track selected category title
  const [isOpen, setIsOpen] = useState(false) // Track panel state
  const auth = useAuth()
  const categoryParam = useSearchParamValue('category')
  const t = useTranslation()

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await PTRestAPI.ListCategories(auth)

        // Add the "All" category
        data.categories.unshift({ categoryID: null, title: 'All' })

        setCategories(data.categories)
      } catch (error) {
        console.error('Error fetching categories:', error)
      }
    }

    fetchCategories()
  }, [auth])

  useEffect(() => {
    if (categoryParam) {
      setSelectedCategoryID(categoryParam)
    }
  }, [categoryParam])

  useEffect(() => {
    if (!categories || !categories.length) return
    //find the category that matches the categoryID
    const category = categories.find((c) => c.categoryID === categoryID)
    if (!category) return
    setSelectedCategoryID(category.categoryID)
    setSelectedCategoryTitle(category.title) // Set the selected category title
  }, [categoryID, categories])

  const handleSelectCategory = (category) => {
    setSelectedCategoryID(category.categoryID)
    setSelectedCategoryTitle(category.title) // Set the selected category title
    // Update the category in the query params
    controller.updateQueryParams({ category: category.categoryID })
    onSelectCategory(category)
    setIsOpen(false) // Close the panel after selecting a category
  }

  const toggleOpen = () => {
    setIsOpen(!isOpen)
  }

  return (
    <div
      className="absolute lg:relative w-full lg:w-[200px]"
      style={{ zIndex: 100 }}
    >
      {/* One-line panel with hover effect for screens md and smaller */}
      <div
        className="block lg:hidden absolute left-0 right-0 sm:max-w-[580px] md:max-w-[950px] sm:mx-auto md:mx-0"
        style={{ zIndex: 110 }}
      >
        <div className="cursor-pointer w-full" onClick={toggleOpen}>
          <div className="flex items-center justify-between gap-4 bg-paper px-8 py-2">
            <Typography className="flex-1 text-xl text-text font-bold">
              {t`Categories`}
            </Typography>
            <Typography className=" text-lg text-text">
              {selectedCategoryTitle || 'All'}
            </Typography>
            {isOpen ? (
              <ChevronUpIcon className="text-text w-5 h-5 ml-2" />
            ) : (
              <ChevronDownIcon className="text-text w-5 h-5 ml-2" />
            )}
          </div>
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute left-0 top-full mt-1 bg-paper origin-top w-full max-h-64 overflow-y-auto shadow-lg rounded-md"
              >
                <ul className="p-2">
                  {categories.map((category) => (
                    <li key={category.categoryID}>
                      <button
                        onClick={() => handleSelectCategory(category)}
                        className={`
                          hover:bg-canvas text-left w-full text-text my-1 text-base p-2 rounded transition-colors duration-100 ${
                            selectedCategoryID === category.categoryID
                              ? 'bg-canvas'
                              : 'bg-paper'
                          }`}
                      >
                        {category.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Original state of the component for screens lg and larger */}
      <div className="hidden lg:block w-[200px]">
        <div className="w-[200px] max-h-[900px] bg-paper p-4 mt-5">
          <h2 className="text-text font-bold mb-4 text-2xl">Categories</h2>
          <ul>
            {categories.map((category) => (
              <li key={category.categoryID} className="mb-2">
                <button
                  onClick={() => handleSelectCategory(category)}
                  className={`
                    hover:bg-canvas text-left w-full text-text my-1 text-base p-2 rounded transition-colors duration-100 ${
                      selectedCategoryID === category.categoryID
                        ? 'bg-canvas'
                        : 'bg-paper'
                    }`}
                >
                  {category.title}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

export default CategoryList
